import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const S = {
  page: { padding: "0 0 80px 0", fontFamily: "system-ui, sans-serif", background: "#f5f5f5", minHeight: "100vh" },
  header: { background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "#fff", padding: "1rem 1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center" },
  title: { margin: 0, fontSize: "1.1rem", fontWeight: 700 },
  back: { color: "#fff", textDecoration: "none", fontSize: "0.85rem", opacity: 0.9 },
  totalBox: { background: "#fff", margin: "1rem", borderRadius: 12, padding: "1rem 1.25rem", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" },
  totalLabel: { color: "#6b7280", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: "0.05em" },
  totalValue: { fontSize: "1.5rem", fontWeight: 700, color: "#667eea" },
  searchBox: { margin: "0 1rem 0.75rem", padding: "0.6rem 0.9rem", border: "1px solid #e5e7eb", borderRadius: 8, width: "calc(100% - 2rem)", fontSize: "0.9rem", outline: "none", boxSizing: "border-box" },
  list: { padding: "0 1rem" },
  card: { background: "#fff", borderRadius: 12, padding: "1rem", marginBottom: "0.75rem", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" },
  row: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" },
  name: { fontWeight: 700, fontSize: "0.95rem", color: "#111" },
  balance: { fontWeight: 700, fontSize: "0.95rem", color: "#10b981" },
  meta: { display: "flex", flexDirection: "column", gap: "0.2rem" },
  metaItem: { fontSize: "0.75rem", color: "#6b7280" },
  metaVal: { color: "#374151", fontWeight: 500 },
  actions: { display: "flex", gap: "0.5rem", marginTop: "0.6rem", borderTop: "1px solid #f3f4f6", paddingTop: "0.6rem" },
  delBtn: { flex: 1, padding: "0.4rem", borderRadius: 6, border: "none", background: "#fee2e2", color: "#dc2626", fontWeight: 600, fontSize: "0.78rem", cursor: "pointer" },
  empty: { textAlign: "center", color: "#9ca3af", padding: "2rem" },
};

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const adminToken = localStorage.getItem("adminToken");

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/users?limit=10000`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setUsers(Array.isArray(res.data) ? res.data : res.data.users || []);
    } catch { setError("Failed to fetch users"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      fetchUsers();
    } catch { setError("Failed to delete user"); }
  };

  const totalBalance = users.reduce((s, u) => s + parseFloat(u.wallet_balance || 0), 0);
  const filtered = users.filter((u) =>
    !search ||
    (u.username || "").toLowerCase().includes(search.toLowerCase()) ||
    String(u.telegram_id || "").includes(search) ||
    (u.phone || "").includes(search) ||
    String(u.player_id || "").includes(search)
  );

  const PAGE_SIZE = 50;
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pages);
  const pageRows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (loading) return <div style={{ padding: "2rem", textAlign: "center", color: "#888" }}>Loading...</div>;
  if (error)   return <div style={{ padding: "2rem", textAlign: "center", color: "#ef4444" }}>{error}</div>;

  return (
    <div style={S.page}>
      <header style={S.header}>
        <h1 style={S.title}>👥 Players ({users.length})</h1>
        <Link to="/dashboard" style={S.back}>← Back</Link>
      </header>

      {/* Summary boxes */}
      <div style={{ display: "flex", gap: "0.75rem", padding: "0.75rem 1rem" }}>
        <div style={{ flex: 1, background: "#fff", borderRadius: 12, padding: "0.75rem 1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderTop: "3px solid #667eea" }}>
          <div style={{ fontSize: "0.65rem", color: "#9ca3af", textTransform: "uppercase" }}>Total Players</div>
          <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#667eea" }}>{users.length}</div>
        </div>
        <div style={{ flex: 1, background: "#fff", borderRadius: 12, padding: "0.75rem 1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", borderTop: "3px solid #10b981" }}>
          <div style={{ fontSize: "0.65rem", color: "#9ca3af", textTransform: "uppercase" }}>Total Balance</div>
          <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#10b981" }}>{totalBalance.toFixed(0)} Birr</div>
        </div>
      </div>

      <input
        style={S.searchBox}
        placeholder="🔍 Search by name, TG ID, or phone..."
        value={search}
        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
      />

      {/* Table */}
      <div style={{ margin: "0 1rem", background: "#fff", borderRadius: 12, overflow: "auto", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
        {filtered.length === 0 ? (
          <div style={S.empty}>No players found.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                <th style={{ padding: "0.6rem 0.5rem", textAlign: "left", color: "#6b7280", fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", borderBottom: "1px solid #f3f4f6" }}>Name</th>
                <th style={{ padding: "0.6rem 0.5rem", textAlign: "left", color: "#6b7280", fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", borderBottom: "1px solid #f3f4f6" }}>Phone</th>
                <th style={{ padding: "0.6rem 0.5rem", textAlign: "left", color: "#6b7280", fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", borderBottom: "1px solid #f3f4f6" }}>Player ID</th>
                <th style={{ padding: "0.6rem 0.5rem", textAlign: "left", color: "#6b7280", fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", borderBottom: "1px solid #f3f4f6" }}>TG ID</th>
                <th style={{ padding: "0.6rem 0.5rem", textAlign: "right", color: "#6b7280", fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", borderBottom: "1px solid #f3f4f6" }}>Balance</th>
                <th style={{ padding: "0.6rem 0.5rem", textAlign: "center", color: "#6b7280", fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", borderBottom: "1px solid #f3f4f6" }}>Status</th>
                <th style={{ padding: "0.6rem 0.5rem", textAlign: "left", color: "#6b7280", fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", borderBottom: "1px solid #f3f4f6" }}>Joined</th>
                <th style={{ padding: "0.6rem 0.5rem", textAlign: "center", color: "#6b7280", fontWeight: 700, fontSize: "0.65rem", textTransform: "uppercase", borderBottom: "1px solid #f3f4f6" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((u, i) => (
                <tr key={u.id} style={{ borderBottom: "1px solid #f9fafb", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <td style={{ padding: "0.6rem 0.5rem", fontWeight: 600, color: "#111" }}>
                    {u.username || "—"}
                    {u.is_admin && <span style={{ marginLeft: 4, fontSize: "0.6rem", background: "#ede9fe", color: "#7c3aed", borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>ADMIN</span>}
                  </td>
                  <td style={{ padding: "0.6rem 0.5rem", color: "#374151" }}>{u.phone || "—"}</td>
                  <td style={{ padding: "0.6rem 0.5rem", color: "#374151", fontFamily: "monospace", fontSize: "0.72rem" }}>{u.player_id || "—"}</td>
                  <td style={{ padding: "0.6rem 0.5rem", color: "#374151", fontFamily: "monospace", fontSize: "0.72rem" }}>{u.telegram_id || "—"}</td>
                  <td style={{ padding: "0.6rem 0.5rem", textAlign: "right", fontWeight: 700, color: "#10b981" }}>{parseFloat(u.wallet_balance || 0).toFixed(2)}</td>
                  <td style={{ padding: "0.6rem 0.5rem", textAlign: "center" }}>
                    {u.is_active ? (
                      <span style={{ fontSize: "0.6rem", background: "#d1fae5", color: "#059669", borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>ACTIVE</span>
                    ) : (
                      <span style={{ fontSize: "0.6rem", background: "#fee2e2", color: "#dc2626", borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>INACTIVE</span>
                    )}
                  </td>
                  <td style={{ padding: "0.6rem 0.5rem", color: "#6b7280", fontSize: "0.7rem" }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td style={{ padding: "0.6rem 0.5rem", textAlign: "center" }}>
                    {!u.is_admin && (
                      <button onClick={() => handleDelete(u.id)} style={{ padding: "0.2rem 0.6rem", borderRadius: 5, border: "none", background: "#fee2e2", color: "#dc2626", fontWeight: 700, fontSize: "0.7rem", cursor: "pointer" }}>
                        Del
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {pages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.75rem", padding: "0.9rem 1rem" }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={safePage === 1}
            style={{ padding: "0.4rem 0.9rem", borderRadius: 8, border: "1px solid #e5e7eb", background: safePage === 1 ? "#f3f4f6" : "#fff", color: safePage === 1 ? "#9ca3af" : "#374151", fontWeight: 700, fontSize: "0.8rem", cursor: safePage === 1 ? "default" : "pointer" }}
          >← Prev</button>
          <span style={{ fontSize: "0.8rem", color: "#6b7280", fontWeight: 600 }}>
            Page {safePage} / {pages} · {filtered.length} players
          </span>
          <button
            onClick={() => setPage(p => Math.min(pages, p + 1))}
            disabled={safePage === pages}
            style={{ padding: "0.4rem 0.9rem", borderRadius: 8, border: "1px solid #e5e7eb", background: safePage === pages ? "#f3f4f6" : "#fff", color: safePage === pages ? "#9ca3af" : "#374151", fontWeight: 700, fontSize: "0.8rem", cursor: safePage === pages ? "default" : "pointer" }}
          >Next →</button>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
